/**
 * Unified Service Core Module
 * Central export point for all service patterns and utilities
 */

// Core infrastructure
export * from "./types";
export * from "./errors";
export * from "./cache";
export * from "./interceptors";
export * from "./http-client";
export * from "./base-service";
export * from "./service-factory";

// Convenience re-exports for common patterns
export {
  UnifiedServiceError,
  ValidationError,
  AuthenticationError,
  AuthorizationError,
  NotFoundError,
  NetworkError,
  TimeoutError,
  RateLimitError,
  ServerError,
  ErrorFactory,
  ErrorHandler,
} from "./errors";

export { ServiceCache, CacheManager, cacheManager } from "./cache";

export {
  InterceptorManager,
  AuthInterceptor,
  LoggingInterceptor,
  TimingInterceptor,
  SecurityInterceptor,
  RetryInterceptor,
  createDefaultInterceptorManager,
} from "./interceptors";

export {
  HttpClient,
  httpClient,
  httpClientAuth,
  httpClientProxy,
} from "./http-client";

export { BaseService, ServiceRegistry, serviceRegistry } from "./base-service";

export {
  UnifiedServiceFactory,
  ServiceBuilder,
  serviceFactory,
  createServiceConfig,
  registerDefaultServices,
} from "./service-factory";

// Type guards and utilities
export const isServiceError = (error: any): error is UnifiedServiceError => {
  return error instanceof Error && "code" in error && "timestamp" in error;
};

export const isValidationError = (error: any): error is ValidationError => {
  return isServiceError(error) && error.code === "VALIDATION_ERROR";
};

export const isAuthError = (error: any): error is AuthenticationError => {
  return (
    isServiceError(error) &&
    (error.code === "AUTHENTICATION_ERROR" ||
      error.code === "AUTHORIZATION_ERROR")
  );
};

export const isNetworkError = (error: any): error is NetworkError => {
  return (
    isServiceError(error) &&
    (error.code === "NETWORK_ERROR" || error.code === "TIMEOUT_ERROR")
  );
};

// Service configuration helpers
export const createProductionConfig = (name: string) => {
  return createServiceConfig(name)
    .timeout(8000)
    .retries(3)
    .retryDelay(1000)
    .caching(true, 10 * 60 * 1000, "cache-first")
    .logging(false) // Disable debug logging in production
    .metrics(true)
    .build();
};

export const createDevelopmentConfig = (name: string) => {
  return createServiceConfig(name)
    .timeout(8000)
    .retries(1)
    .retryDelay(500)
    .caching(false) // Disable caching in development
    .logging(true)
    .metrics(true)
    .build();
};

export const createTestConfig = (name: string) => {
  return createServiceConfig(name)
    .timeout(8000)
    .retries(0)
    .caching(false)
    .logging(false)
    .metrics(false)
    .build();
};

// Global service management utilities
export const healthCheckAllServices = async (): Promise<
  Record<string, any>
> => {
  return serviceFactory.healthCheckAll();
};

export const getServiceMetrics = async (): Promise<Record<string, any>> => {
  return serviceFactory.getAllMetrics();
};

export const clearAllServiceCaches = async (
  pattern?: string,
): Promise<Record<string, number>> => {
  return serviceFactory.clearAllCaches(pattern);
};

export const shutdownAllServices = async (): Promise<void> => {
  return serviceFactory.shutdown();
};

// Common validation schemas
export const commonValidationRules = {
  email: {
    field: "email",
    type: "string" as const,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  phone: {
    field: "phone",
    type: "string" as const,
    pattern: /^\+?[\d\s\-\(\)]+$/,
  },
  password: {
    field: "password",
    type: "string" as const,
    min: 8,
  },
  uuid: {
    field: "id",
    type: "string" as const,
    pattern:
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  },
  pagination: {
    page: {
      field: "page",
      type: "number" as const,
      min: 1,
    },
    limit: {
      field: "limit",
      type: "number" as const,
      min: 1,
      max: 100,
    },
  },
};

// Default service instances (backward compatibility)
export { httpClient as defaultHttpClient } from "./http-client";
export { serviceRegistry as defaultServiceRegistry } from "./base-service";
export { cacheManager as defaultCacheManager } from "./cache";
